<template>
  <div class="table-share-modal">
    <CustomModal @onClose="close">
      <div class="table-share-modal-content-wrapper" ref="shareModal">
        <div class="content">
          <div>
            <h3 class="content-title">
              Datenbank <b>{{ database.caption }}</b> Teilen
            </h3>
            <p class="content-paragraph">
              Teilen Sie Ihre Datenbank mit anderen Personen.
            </p>
          </div>

          <form @submit.prevent="save()">
            <div class="input-field">
              <input
                class="email-input"
                placeholder="E-Mail-Adresse"
                v-model="shareParams.email"
                type="email"
                required
              />
              <CustomDropdown className="permission-selector form-input">
                <template v-slot:selected-option>
                  <span class="line-clamp"
                    >{{
                      getShareParamsAccessLevelName(shareParams.accessLevel)
                    }}
                  </span>
                </template>
                <template v-slot:option-data>
                  <b-dropdown-item
                    v-for="(level, index) in getDropDownAccessLevels"
                    :key="level.name"
                    @click="setShareParamsAccessLevel(level.name)"
                    :active="shareParams.accessLevel === level.name"
                  >
                    <template v-if="index !== 0">
                      {{ level.caption }}
                    </template>
                    <template v-if="index === 0">
                      <div class="d-flex justify-content-between w-100">
                        <span>{{ level.caption }}</span>

                        <span>
                          <img
                            class="button-icon"
                            :src="require('../../assets/img/chevron-down.svg')"
                        /></span>
                      </div>
                    </template>
                  </b-dropdown-item>
                </template>
              </CustomDropdown>
            </div>
            <div class="form-element-wrapper">
              <CommonButton
                class="common-button-component"
                :isLoading="isLoadingShareButton"
                :isDisabled="isLoadingShareButton"
              >
                Teilen
              </CommonButton>
            </div>
          </form>
          <div class="table-share-contact-info-wrap">
            <div v-if="tablePolicies.length > 0 && !isLoadingTablePolicies">
              <p class="user-access-desc">
                Diese Personen haben bereits Zugriff:
              </p>
              <div
                v-if="tablePolicies.length > 0"
                class="table-share-contact-info"
                @scroll="onScrollUserIdentity"
              >
                <div
                  class="user-identity"
                  v-for="p in getSortedTablePolicies"
                  :key="p.id"
                >
                  <p>{{ p.email }}</p>
                  <CustomDropdown
                    :disabled="p.email == 'public'"
                    className="permission-selector"
                    v-if="p.access_level != 'owner'"
                    ref="userIdentityPermissionSelector"
                    :uniqueReferenceId="p.id"
                    @onDropdownOpen="onDropdownOpen"
                    @onDropdownClose="onDropdownClose"
                  >
                    <template v-slot:selected-option>
                      <span class="line-clamp"
                        >{{ getShareParamsAccessLevelName(p.access_level) }}
                      </span>
                    </template>
                    <template v-slot:option-data>
                      <b-dropdown-item
                        v-for="level in getDropDownAccessLevels"
                        :key="level.name"
                        @click="changePolicy(p, level.name)"
                        :active="p.access_level === level.name"
                      >
                        {{ level.caption }}
                      </b-dropdown-item>
                    </template>
                  </CustomDropdown>
                  <span class="owner-caption" v-else>Besitzer</span>
                  <img
                    class="remove-policy"
                    :src="require('@/assets/img/trash.svg')"
                    @click="removePolicy(p)"
                    v-if="p.access_level != 'owner' && !isCurrentUser(p.email)"
                  />
                  <div v-if="p.id === shareLoadingId" class="loadingoverlay">
                    <div class="custom-table-progress-icon"></div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="isLoadingTablePolicies" class="loading-wrapper">
              <LoadingSpinner />
            </div>
          </div>
        </div>
      </div>
    </CustomModal>
  </div>
</template>

<script>
import CustomModal from './CustomModal.vue';
import CommonButton from '../widgets/CommonButton.vue';
import LoadingSpinner from '../widgets/LoadingSpinner.vue';
import {
  getDataBaseAccessRights,
  shareDataBaseTable,
} from '../../services/userTable';

import { deleteTablePolicy } from '@/services/axios/data.api';

import CustomDropdown from '../widgets/CustomDropdown.vue';
export default {
  name: 'tableShareModal',
  data() {
    return {
      accessLevels: [
        {
          name: 'read',
          caption: 'Kann lesen',
        },
        {
          name: 'write',
          caption: 'Kann bearbeiten',
        },
        {
          name: 'full_access',
          caption: 'Hat Vollzugriff',
        },
        {
          name: 'owner',
          caption: 'Besitzer',
        },
      ],

      shareParams: {
        email: null,
        accessLevel: 'read',
      },
      tablePolicies: [],
      isLoadingTablePolicies: false,
      isUserIdentityDropdownOpen: false,
      isLoadingShareButton: false,
      shareLoadingId: null,
    };
  },
  props: {
    database: { type: Object, required: true },
  },
  methods: {
    close() {
      this.$emit('close');
    },
    async save() {
      try {
        this.isLoadingShareButton = true;
        await this.shareTable({
          recipient: this.shareParams.email,
          access_level: this.shareParams.accessLevel,
        });
        this.$emit('save');
        this.$set(this.shareParams, 'email', null);
        this.showToaster(
          'Datenbank wurde erfolgreich geteilt',
          'Erfolg',
          'success'
        );
      } catch (error) {
        this.showToaster(error.message, 'Fehlerhaft !', 'danger');
      } finally {
        this.isLoadingShareButton = false;
      }
    },
    async shareTable({ recipient, access_level }) {
      try {
        const payload = {
          recipient,
          access_level,
        };
        const response = await shareDataBaseTable(this.database.name, payload);

        if (response && response.status === 200 && response.data) {
          this.getAccessRights();
        }
      } catch (error) {
        if (error && error.status === 400 && error.data && error.data.message) {
          let message = error.data.message;
          if (message === 'User is owner') {
            this.showToaster(
              'Dieser User hat die Datenbank erstellt, du kannst ihn nicht einladen',
              'Fehlerhaft !',
              'danger'
            );
          } else if (message === 'E-mail not found') {
            this.showToaster(
              'Die angegebene Email-Adresse konnte nicht gefunden werden.',
              'Fehlerhaft !',
              'danger'
            );
          }
        } else if (error.status === 404) {
          this.showToaster(
            'Die angegebene Email-Adresse konnte nicht gefunden werden.',
            'Fehlerhaft !',
            'danger'
          );
        }
        throw error;
      }
    },
    async getAccessRights() {
      try {
        const response = await getDataBaseAccessRights(this.database.name);

        if (
          response &&
          response.status === 200 &&
          response.data &&
          response.data.length > 0
        ) {
          this.tablePolicies = response.data;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log({ error });
      }
    },

    async removePolicy(p) {
      try {
        this.shareLoadingId = p.id;
        const payload = {
          policy_id: p.id,
        };
        console.log('1');
        const response = await deleteTablePolicy(this.database.name, payload);
        if (response && response.status === 200 && response.data) {
          await this.getAccessRights();
        }
        this.showToaster(
          'Die Berechtigung wurde erfolgreich entfernt',
          'Erfolg',
          'success'
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
      } finally {
        this.shareLoadingId = null;
      }
    },
    changePolicy: async function (p, accessName) {
      try {
        this.shareLoadingId = p.id;
        await this.shareTable({ recipient: p.email, access_level: accessName });
        this.showToaster(
          'Die Berechtigung wurde erfolgreich angepasst',
          'Erfolg',
          'success'
        );
      } catch (error) {
        this.showToaster(error.message, 'Fehlerhaft !', 'danger');
      } finally {
        this.shareLoadingId = null;
      }
    },
    setShareParamsAccessLevel(accessName) {
      this.shareParams.accessLevel = accessName;
    },
    getShareParamsAccessLevelName(accessLevel) {
      return this.accessLevels.find((level) => level.name == accessLevel)
        .caption;
    },
    isCurrentUser(userEmail) {
      return this.user.attributes.email === userEmail;
    },
    async loadTablePolicies() {
      try {
        this.isLoadingTablePolicies = true;
        await this.getAccessRights();
      } catch (error) {
        //
      } finally {
        this.isLoadingTablePolicies = false;
      }
    },

    onScrollUserIdentity() {
      if (this.isUserIdentityDropdownOpen) {
        this.$refs.userIdentityPermissionSelector.forEach((element) => {
          element.closeDropdown();
        });
      }
    },

    onDropdownOpen() {
      this.isUserIdentityDropdownOpen = true;
    },
    onDropdownClose() {
      this.isUserIdentityDropdownOpen = false;
    },
    showToaster(message, title, variant) {
      this.$bvToast.toast(message, {
        title,
        variant,
      });
    },
  },
  created() {
    this.loadTablePolicies();
  },
  computed: {
    getSortedTablePolicies() {
      return [...this.tablePolicies].sort((a, b) => {
        if (a.access_level === 'owner') return -1;
        if (b.access_level === 'owner') return 1;
        return 0;
      });
    },
    getDropDownAccessLevels() {
      return this.accessLevels.filter((el) => el.name !== 'owner');
    },

    user() {
      return this.$store.state.user;
    },
  },

  components: {
    CustomModal,
    CommonButton,
    LoadingSpinner,
    CustomDropdown,
  },
};
</script>

<style scoped>
.table-share-modal .table-share-modal-content-wrapper {
  height: 554px;
  max-width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-inline: 98px;
  padding-block: 70px;
}
@media (max-width: 767px) {
  .table-share-modal .table-share-modal-content-wrapper {
    padding-inline: 40px;
  }
}
.table-share-modal .content {
  width: 100%;
  height: fit-content;
}

.table-share-modal .content-title {
  font-size: 20px;
  color: #333343;
  margin: 0;
  font-weight: 400;
  text-transform: uppercase;
  padding: 0 6px;
}

.table-share-modal .content-paragraph {
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 6px;
  color: #333343;
  margin: 20px 0;
  padding: 0 6px;
}

.table-share-modal .email-input {
  outline: none;
  border: 0.5px solid #333343;
  border-radius: 4px;
  width: 330px;
  height: 34px;
  font-size: 16px;
  padding: 4px 16px;
  font-weight: 400;
  line-height: 20px;
  color: rgba(51, 51, 51);
}

@media (max-width: 767px) {
  .table-share-modal .email-input {
    width: 100%;
    max-width: 100%;
  }
}
.table-share-modal .email-input::placeholder {
  color: rgba(51, 51, 51, 0.24);
}
.table-share-modal .form-element-wrapper {
  padding-top: 36px;
  position: relative;
}

.table-share-modal .custom-dropdown.permission-selector {
  border-radius: 4px;
  font-size: 13px;
  width: 155px;
  display: flex;
  height: 100%;
}
.table-share-modal
  ::v-deep
  .custom-dropdown.permission-selector.form-input
  .custom-dropdown-toggle {
  height: 34px;
}

.table-share-modal .user-access-desc {
  font-size: 16px;
  color: #333343;
  padding: 0 6px;
}

.table-share-modal .table-share-contact-info {
  height: 150px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.table-share-modal .content .user-identity {
  padding: 6px;
  display: flex;
  align-items: center;
  gap: 9px;
  justify-content: flex-start;
  width: 100%;
  font-size: 16px;
  position: relative;
}

.table-share-modal .content .user-identity > p {
  display: inline-block;
  margin-top: 0px;
  margin-bottom: 0px;
  width: 60%;
  color: #333343;
  overflow-wrap: anywhere;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 300px;
}

.table-share-modal .content .user-identity > i {
  /* margin-left: 8px; */
  cursor: pointer;
  width: 5%;
  color: #333343;
}

.table-share-modal .content .user-identity .owner-caption {
  color: #333343;
  font-size: 14px;
  padding: 0;
  width: auto;
  display: inline-block;
}
.table-share-modal .user-identity ::v-deep button {
  /* padding: 0; */
  line-height: 16px;
  justify-content: space-between;
}

.table-share-modal .content .user-identity .line-clamp {
  /* width: 110px; */
  text-align: left;
  max-width: 150px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}
@media (max-width: 525px) {
  .table-share-modal .content .user-identity .line-clamp {
    max-width: 70px;
  }
}
.table-share-contact-info-wrap {
  margin-top: 27px;
  height: 187px;
}

::v-deep .dropdown-item.active {
  background: #e5e5e591;
}

.table-share-modal .loading-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}
.table-share-modal ::v-deep .loading-wrapper .progress-icon-wrapper {
  position: unset;
}

.table-share-modal
  ::v-deep
  .permission-selector.form-input
  .custom-dropdown-toggle
  img {
  margin-top: 3px;
}

.table-share-modal
  .user-identity
  ::v-deep
  .permission-selector
  .custom-dropdown-toggle
  img {
  margin-top: 1px;
}

.table-share-modal ::v-deep .permission-selector.active .button-icon {
  transform: rotate(180deg);
  width: 9px;
}

.table-share-modal .user-identity ::v-deep .custom-dropdown-menu li {
  width: 100%;
  height: 28px;
  display: flex;
  align-items: center;
}

.table-share-modal .user-identity ::v-deep .custom-dropdown-toggle {
  padding-inline: 12px;
}

.table-share-modal form {
  padding: 0 6px;
}
.table-share-modal .input-field {
  display: flex;
  align-items: center;
  gap: 8px;
}

.table-share-modal .remove-policy {
  height: 14px;
  cursor: pointer;
}

@media (max-width: 767px) {
  .table-share-modal .input-field {
    flex-direction: column;
    align-items: flex-start;
  }
}
.table-share-modal .loadingoverlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #1f2532c7;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 14;
  text-align: center;
  border-radius: 4px;
}
.table-share-modal .loadingoverlay .custom-table-progress-icon {
  content: ' ';
  display: block;
  width: 30px;
  height: 30px;
  margin: 1px;
  border-radius: 50%;
  border: 2px solid #fff;
  border-color: #fff transparent #fff transparent;
  animation: progress-icon-animation 1.2s linear infinite;
  margin: 0 auto;
  position: absolute;
  left: 50%;
}
</style>
